import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ExternalLink,
  Check,
  Shield,
  GitPullRequest,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function NotificationBell() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async (signal) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/impact-definitions/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      // Aborted polls (navigation/unmount) and transient network cancellations are
      // expected for a background 30s poller. The browser reports a cancelled
      // fetch as TypeError "Failed to fetch" (and PostHog's fetch wrapper drops the
      // AbortError name), so treat both as expected. Log only genuine errors.
      const expected = error.name === "AbortError" || error.message === "Failed to fetch";
      if (!expected) {
        console.error("Error fetching notifications:", error);
      }
    }
  }, [token]);

  useEffect(() => {
    const controller = new AbortController();
    fetchNotifications(controller.signal);
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => fetchNotifications(controller.signal), 30000);
    return () => { clearInterval(interval); controller.abort(); };
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/impact-definitions/notifications/${notificationId}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/impact-definitions/notifications/mark-all-read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      setOpen(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "impact_definition_update":
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case "bia_update_reminder":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "gap_review_request":
        return <GitPullRequest className="h-4 w-4 text-red-500" />;
      case "gap_bcm_alert":
        return <Shield className="h-4 w-4 text-amber-500" />;
      case "gap_approved":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "gap_rejected":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case "rto_conflict":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Bell className="h-4 w-4 text-slate-500" />;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-red-500"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead}>
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-4 py-3 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                  !notification.read ? "bg-blue-50/50" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium truncate ${!notification.read ? "text-foreground" : "text-muted-foreground"}`}>
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <div className="h-2 w-2 rounded-full bg-blue-500 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </span>
                      {notification.action_url && (
                        <span className="text-xs text-primary flex items-center gap-1">
                          View <ExternalLink className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
